import { useEffect } from 'react';
import { LanguageProvider, useLanguageContext } from './i18n/language-context';
import './i18n/i18n';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { Toaster } from 'components/ui/toaster';
import { ToastAction } from 'components/ui/toast';
import { toast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { startVersionCheck } from 'utils/version-check';
import { ClientMiddleware } from 'state/client-middleware';
import MainLayout from 'pages/main/main-layout';
import AuthLayout from 'pages/auth/auth-layout';
import { SigninPage } from 'pages/auth/signin/signin-page';
import { SigninOidcCallbackPage } from 'pages/auth/signin/signin-oidc-callback';
import { SignupPage } from 'pages/auth/signup/signup-page';
import { EmailVerification } from 'pages/auth/email-verification/email-verification';
import { SetPasswordPage } from './pages/auth/set-password/set-password';
import { ActivationSuccess } from './pages/auth/activation-success/activation-success';
import { VerificationFailed } from './pages/auth/verification-failed/verification-failed';
import { ResetPasswordPage } from './pages/auth/reset-password/reset-password';
import { ForgotPasswordPage } from './pages/auth/forgot-password/forgot-password';
import { IamTablePage } from './pages/main/iam-table';
import { Profile } from './pages/profile/profile';
import { ThemeProvider } from './components/core/theme-provider';
import { VerifyOtpKey } from './pages/auth/verify-otp-key/verify-otp-key';
import ChatHistoryPage from 'pages/chat-history/chat-history-page';
import ChatPage from 'pages/chat/chat';
import ProjectChatPage from 'pages/chat/project-chat';
import HomePage from './pages/home/home';
import SignupEmailSentPage from 'pages/auth/signup-email-sent/signup-email-sent';
import { RedirectPage } from './pages/auth/redirect/redirect';
import GroupInvitePage from 'pages/group-invite/group-invite';
import { LoadingOverlay } from './components/core/loading-overlay';
import { NoAuthMiddleware } from './state/no-auth-middleware';
import UserHolder from 'pages/user-holder';
import { PostAuthMiddleware } from './state/post-auth-middleware';
import { NotificationMiddleware } from './state/notification-middleware';
// import { CollaborationWebSocketMiddleware } from './state/collaboration-websocket-middleware';
import { OrganizationTablePage } from './pages/main/organization-table';
import { SubscriptionRouteGuard } from './components/core/subscription-route-guard';
import { SignupRouteGuard } from './components/core/signup-route-guard';
import { ComingSoonRouteGuard } from './components/core/coming-soon-route-guard';
import { RoleRouteGuard } from './components/core/role-route-guard';
import { SubscriptionPlansPage } from './pages/subscription/subscription-page';
import { CheckoutPlanPage } from './pages/checkout/checkout-plan';
import { PaymentSuccessPage } from './pages/payment/payment-success';
import { PaymentCancelledPage } from './pages/payment/payment-cancelled';
import { PaymentFailedPage } from './pages/payment/payment-failed';
import { FileManagerMyFiles } from './pages/file-manager/my-files';
import ProjectPage from './pages/project/project-page';
// import DocumentManagementPage from './pages/document-manager/document-manager';
import PdfRouteViewer from './features/file-manager/components/pdf-route-viewer';
// import { usePageTracking, useSessionTracker, useTrackPageLoadComplete } from './analytics/tracker';
import CustomGptPage from 'pages/custom-gpt/custom-gpt-page';
import ComingSoonPage from 'pages/coming-soon/coming-soon';
import AgentPage from 'pages/agents/agents-page';
import AgentDetailsPage from 'pages/agents/agent-details-page';
import ToolsPage from 'pages/tools/tools-page';
import CreateAPIWrapper from 'pages/tools/create-api/create-api';
import ToolDetailsPage from 'pages/tools/tool-details-page';
import KnowledgeBasePage from 'pages/knowledge-base/knowledge-base-page';
import KnowledgeBaseFolderPage from 'pages/knowledge-base/knowledge-base-folder-page';
import PromptsPage from 'pages/prompts/prompts-page';
import PromptsFolderPage from 'pages/prompts/prompts-folder-page';
import ModelsPage from 'pages/models';
import OAuthCompletePage from 'pages/integrations/oauth-complete-page';
import ComposioOauthCallbackPage from 'pages/tools/composio-oauth-callback-page';
import ProviderModelsPage from 'pages/models/provider-models-page';
import UsagePage from 'pages/usage/usage-page';
import SkillsPage from 'pages/skills';
import { ConfigureDashboardPage } from 'pages/configure';
import { isGroupChatEnabled } from './config/feature-flags';
import { useAuthStore } from './state/store/auth/useAuthStore';
import { useSubscriptionStore } from './state/store/subscription';
import SchedulerPage from 'pages/scheduler';
import { completeLogout } from 'features/auth/utils/complete-logout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: 0,
      refetchOnWindowFocus: false,
    },
  },
});

function ExternalRedirect({ to }: { to: string }) {
  window.location.replace(to);
  return null;
}

function IntegrationsToKnowledgeBase() {
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  next.set('tab', 'integrations');
  return <Navigate to={{ pathname: '/knowledge-base', search: `?${next.toString()}` }} replace />;
}

function AppContent() {
  const { isLoading } = useLanguageContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // usePageTracking();
  // useTrackPageLoadComplete();
  // useSessionTracker();

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'subscription-storage') {
        void useSubscriptionStore.persist.rehydrate();
        void queryClient.invalidateQueries({ queryKey: ['current-subscription'] });
        return;
      }

      if (event.key !== 'auth-storage') {
        return;
      }

      if (event.newValue === null) {
        if (useAuthStore.getState().isAuthenticated) {
          completeLogout(queryClient, navigate);
        }
        return;
      }

      // Another tab logged in — reload auth state from localStorage so ClientMiddleware can redirect.
      void useAuthStore.persist.rehydrate();
    };

    globalThis.addEventListener('storage', handleStorageChange);

    return () => {
      globalThis.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);

  useEffect(
    () =>
      startVersionCheck(() => {
        const refreshLabel = t('REFRESH', { defaultValue: 'Refresh' });
        toast({
          title: t('NEW_VERSION_AVAILABLE_REFRESH', {
            defaultValue: 'A new version is available — refresh to update',
          }),
          action: (
            <ToastAction altText={refreshLabel} onClick={() => window.location.reload()}>
              {refreshLabel}
            </ToastAction>
          ),
        });
      }),
    [t]
  );

  if (isLoading) {
    return <LoadingOverlay />;
  }

  const isOAuthCompleteRoute = location.pathname === '/integrations/oauth-complete';
  const isComposioOauthCallbackRoute = location.pathname === '/composio/oauth/callback';
  const isStandaloneOauthRoute = isOAuthCompleteRoute || isComposioOauthCallbackRoute;

  return (
    <div className="min-h-screen bg-background font-sans antialiased">
      {/* <RedirectHandler /> */}
      <ClientMiddleware>
        <ThemeProvider defaultTheme="system">
            {isStandaloneOauthRoute ? (
              <Routes>
                <Route path="/integrations/oauth-complete" element={<OAuthCompletePage />} />
                <Route path="/composio/oauth/callback" element={<ComposioOauthCallbackPage />} />
              </Routes>
            ) : (
              <Routes>
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={<SigninPage />} />
                  <Route path="/login/callback" element={<SigninOidcCallbackPage />} />
                  <Route path="/signup" element={
                    <SignupRouteGuard>
                      <SignupPage />
                    </SignupRouteGuard>
                  } />
                  <Route path="/sent-email" element={<EmailVerification />} />
                  <Route path="/activate" element={<SetPasswordPage />} />
                  <Route path="/resetpassword" element={<ResetPasswordPage />} />
                  <Route path="/success" element={<ActivationSuccess />} />
                  <Route path="/activate-failed" element={<VerificationFailed />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/verify-key" element={<VerifyOtpKey />} />
                  <Route path="/signup-email-sent" element={
                    <SignupRouteGuard>
                      <SignupEmailSentPage />
                    </SignupRouteGuard>
                  } />
                  <Route path="/redirect" element={<RedirectPage />} />
                </Route>
                {isGroupChatEnabled && (
                  <Route path="/group-invite/:token" element={<GroupInvitePage />} />
                )}
                <Route
                  path="/promotion"
                  element={
                    <ExternalRedirect to={process.env.REACT_APP_PUBLIC_PROMOTION_PAGE_URL ?? ''} />
                  }
                />
                <Route element={
                  <PostAuthMiddleware>
                    <NotificationMiddleware>
                      <UserHolder />
                    </NotificationMiddleware>
                  </PostAuthMiddleware>
                }>
                  <Route element={<RoleRouteGuard />}>
                  <Route element={<MainLayout />}>
                    <Route element={<ComingSoonRouteGuard />}>
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/identity-management" element={<IamTablePage />} />
                      <Route path="/chat-history" element={<ChatHistoryPage />} />
                      <Route path="/chat" element={<ChatPage />} />
                      <Route path="/chat/:conversationId/:projectId" element={<ProjectChatPage />} />
                      <Route path="/chat/:conversationId" element={<ChatPage />} />
                      <Route path="/project/:projectId" element={<ProjectPage />} />
                      <Route path="/custom-gpts/:customGptId" element={<CustomGptPage />} />
                      <Route path="/organization-management" element={<OrganizationTablePage />} />
                      <Route path="/media-library" element={<FileManagerMyFiles />} />
                      <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
                      <Route path="/knowledge-base/:folderId" element={<KnowledgeBaseFolderPage />} />
                      {/* <Route path="/models" element={<ModelsPage />} />
                      <Route path="/models/:provider" element={<ProviderModelsPage />} /> */}
                      <Route path="/usage" element={<UsagePage />} />
                      <Route path="/comingsoon" element={<ComingSoonPage />} />
                      <Route path="/agents" element={<AgentPage />} />
                      <Route path="/tools" element={<ToolsPage />} />
                      <Route path="/tool-details/:toolId" element={<ToolDetailsPage />} />
                      <Route path="/tool-details/:toolId/create-api" element={<CreateAPIWrapper />} />
                      <Route path="/agent-details/:agentId" element={<AgentDetailsPage />} />
                      <Route path="/agent-details/:agentId/edit" element={<AgentDetailsPage />} />
                      <Route path="/agent-details/:agentId/configuration" element={<AgentDetailsPage />} />
                      <Route path="/agent-details/:agentId/retrieval-testing" element={<AgentDetailsPage />} />
                      <Route path="/agent-details/:agentId/integrations" element={<AgentDetailsPage />} />
                      <Route path="/agent-details/:agentId/conversations" element={<AgentDetailsPage />} />
                      <Route path="/skills" element={<SkillsPage />} />
                      <Route path="/workflows" element={<ComingSoonPage />} />
                      <Route path="/prompts" element={<PromptsPage />} />
                      <Route path="/prompts/:folderId" element={<PromptsFolderPage />} />
                      <Route path="/integrations" element={<IntegrationsToKnowledgeBase />} />
                      <Route path="/scheduler" element={<SchedulerPage />} />
                      <Route path="/configure" element={<ConfigureDashboardPage />} />
                      <Route path="*" element={<Navigate to="/chat" replace />} />
                    </Route>
                  </Route>
                  <Route path="/subscription-plans" element={
                    <SubscriptionRouteGuard>
                      <SubscriptionPlansPage />
                    </SubscriptionRouteGuard>
                  } />
                  <Route path="/checkout" element={
                    <SubscriptionRouteGuard>
                      <CheckoutPlanPage />
                    </SubscriptionRouteGuard>
                  } />
                  <Route path="/payment/result" element={<PaymentSuccessPage />} />
                  <Route path="/payment-success" element={<PaymentSuccessPage />} />
                  <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
                  <Route path="/payment-failed" element={<PaymentFailedPage />} />
                  {/* <Route path="/home" element={<HomePage />} /> */}
                  <Route path="/" element={<Navigate to="/chat" />} />
                  <Route path="/pdf-viewer" element={<PdfRouteViewer />} />
                  </Route>
                </Route>
              </Routes>
            )}
        </ThemeProvider>
      </ClientMiddleware>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <NuqsAdapter>
        <QueryClientProvider client={queryClient}>
          <NoAuthMiddleware>
            <LanguageProvider defaultLanguage={new URLSearchParams(window.location.search).get('lang') || 'de-DE'} defaultModules={['common', 'auth', 'profile']}>
              <AppContent />
            </LanguageProvider>
          </NoAuthMiddleware>
        </QueryClientProvider>
      </NuqsAdapter>
    </BrowserRouter>
  );
}

// 2 coupon and a  extra card ,subscription page a plan details,

export default App;
